import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2, Pencil, X, QrCode as QrIcon, Phone, Cake, School, Shield, Clock, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const empty = {
  first_name: "", last_name: "", grade: "", parent_id: "",
  home_address: "", home_lat: 46.5083, home_lng: 6.4977,
  birth_date: "", school_name: "École Jules Ferry",
  drop_address: "", drop_lat: 46.5083, drop_lng: 6.4977,
  schedule_morning: "07:30", schedule_evening: "16:45",
  special_needs: "", parent_phone: "", authorized_pickup: "", safety_notes: "",
  photo_url: "",
};

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function AdminChildren() {
  const [children, setChildren] = useState([]);
  const [parents, setParents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showQr, setShowQr] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [c, p] = await Promise.all([api.get("/children"), api.get("/users?role=parent")]);
    setChildren(c.data); setParents(p.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); };
  const close = () => { setEditing(null); setForm(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        home_lat: parseFloat(form.home_lat), home_lng: parseFloat(form.home_lng),
        drop_lat: form.drop_lat ? parseFloat(form.drop_lat) : null,
        drop_lng: form.drop_lng ? parseFloat(form.drop_lng) : null,
      };
      if (editing === "new") await api.post("/children", payload);
      else await api.put(`/children/${editing}`, payload);
      toast.success("Enfant enregistré");
      close(); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!confirm("Supprimer cet enfant ?")) return;
    try { await api.delete(`/children/${id}`); load(); toast.success("Supprimé"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const [geocoding, setGeocoding] = useState(false);
  const geocodeAll = async () => {
    if (!confirm("Géocoder toutes les adresses enfants via Nominatim ? Cela peut prendre 1-2 min.")) return;
    setGeocoding(true);
    try {
      const { data } = await api.post("/admin/geocode-children");
      toast.success(`${data.updated}/${data.total} enfants géocodés (${data.failed} échecs)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGeocoding(false); }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="admin-children-page">
      <header className="mb-8 flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Registre</div>
          <h1 className="font-display font-black uppercase text-4xl lg:text-5xl tracking-tighter">Enfants</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="bulk-geocode-btn" onClick={geocodeAll} disabled={geocoding}
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            {geocoding ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />} Géocoder tout
          </button>
          <button data-testid="add-child-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-[#FFC60A] border border-zinc-900 px-4 py-2.5 font-bold hover:bg-[#ffd43a]">
            <Plus size={16} /> Nouvel enfant
          </button>
        </div>
      </header>

      <div className="border border-zinc-900 overflow-x-auto max-w-7xl">
        <table className="w-full text-sm" data-testid="children-table">
          <thead className="bg-zinc-900 text-white">
            <tr>
              {["Enfant", "Classe / École", "Parent", "Horaires", "Sécurité", "QR", "Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-2 text-[10px] font-mono tracking-widest uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {children.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-zinc-500">Aucun enfant enregistré.</td></tr>
            ) : children.map((c) => (
              <tr key={c.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-zinc-900 text-[#FFC60A] flex items-center justify-center font-display font-black">
                      {c.first_name?.[0]}
                    </div>
                    <div>
                      <button data-testid={`view-child-${c.id}`} onClick={() => setShowDetail(c)} className="font-semibold hover:underline">{c.first_name} {c.last_name}</button>
                      {c.birth_date && <div className="text-[10px] font-mono text-zinc-500"><Cake size={9} className="inline" /> {c.birth_date}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2 text-xs">
                  <div className="font-mono">{c.grade || "—"}</div>
                  <div className="text-zinc-500">{c.school_name || ""}</div>
                </td>
                <td className="px-4 py-2 text-xs">
                  <div>{parents.find((p) => p.id === c.parent_id)?.name || "—"}</div>
                  {c.parent_phone && <div className="text-zinc-500 font-mono"><Phone size={10} className="inline" /> {c.parent_phone}</div>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {c.schedule_morning && <div>↑ {c.schedule_morning}</div>}
                  {c.schedule_evening && <div>↓ {c.schedule_evening}</div>}
                </td>
                <td className="px-4 py-2 text-xs">
                  {c.special_needs || c.safety_notes ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFC60A] border border-zinc-900 text-[10px] font-bold uppercase tracking-widest">
                      <Shield size={10} /> Spécifique
                    </span>
                  ) : <span className="text-zinc-400">—</span>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  <button data-testid={`view-qr-${c.id}`} onClick={() => setShowQr(c)} className="inline-flex items-center gap-1 hover:underline">
                    <QrIcon size={12} /> {c.qr_code}
                  </button>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`edit-child-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white"><Pencil size={12} /></button>
                    <button data-testid={`delete-child-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 border border-zinc-900 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 size={12} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="child-modal">
          <form onSubmit={submit} className="bg-white border border-zinc-900 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">{editing === "new" ? "Nouvel enfant" : "Modifier"}</div>
              <button type="button" onClick={close}><X size={18} /></button>
            </div>
            <div className="p-4 grid sm:grid-cols-2 gap-3">
              <Field label="Prénom"><input data-testid="child-form-first_name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border border-zinc-900 px-3 py-2 focus:ring-2 focus:ring-zinc-900 focus:outline-none" /></Field>
              <Field label="Nom"><input data-testid="child-form-last_name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border border-zinc-900 px-3 py-2 focus:ring-2 focus:ring-zinc-900 focus:outline-none" /></Field>
              <Field label="Date de naissance"><input data-testid="child-form-birth_date" type="date" value={form.birth_date || ""} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Classe"><input data-testid="child-form-grade" value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="École"><input data-testid="child-form-school_name" value={form.school_name || ""} onChange={(e) => setForm({ ...form, school_name: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Photo URL"><input data-testid="child-form-photo_url" value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://..." className="w-full border border-zinc-900 px-3 py-2" /></Field>

              <div className="sm:col-span-2 mt-2"><div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-200 pb-1">Adresses</div></div>
              <Field label="Adresse de prise en charge"><input data-testid="child-form-home_address" value={form.home_address || ""} onChange={(e) => setForm({ ...form, home_address: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Adresse de dépose (école)"><input data-testid="child-form-drop_address" value={form.drop_address || ""} onChange={(e) => setForm({ ...form, drop_address: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <div className="grid grid-cols-2 gap-2"><Field label="Lat domicile"><input data-testid="child-form-lat" type="number" step="0.0001" value={form.home_lat} onChange={(e) => setForm({ ...form, home_lat: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field><Field label="Lng domicile"><input data-testid="child-form-lng" type="number" step="0.0001" value={form.home_lng} onChange={(e) => setForm({ ...form, home_lng: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field></div>
              <div className="grid grid-cols-2 gap-2"><Field label="Lat école"><input type="number" step="0.0001" value={form.drop_lat || ""} onChange={(e) => setForm({ ...form, drop_lat: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field><Field label="Lng école"><input type="number" step="0.0001" value={form.drop_lng || ""} onChange={(e) => setForm({ ...form, drop_lng: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field></div>

              <div className="sm:col-span-2 mt-2"><div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-200 pb-1">Horaires habituels</div></div>
              <Field label="Matin (prise en charge)"><input data-testid="child-form-schedule_morning" type="time" value={form.schedule_morning || ""} onChange={(e) => setForm({ ...form, schedule_morning: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Soir (dépose)"><input data-testid="child-form-schedule_evening" type="time" value={form.schedule_evening || ""} onChange={(e) => setForm({ ...form, schedule_evening: e.target.value })} className="w-full border border-zinc-900 px-3 py-2" /></Field>

              <div className="sm:col-span-2 mt-2"><div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500 border-b border-zinc-200 pb-1">Contacts & sécurité</div></div>
              <Field label="Parent"><select data-testid="child-form-parent" value={form.parent_id || ""} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} className="w-full border border-zinc-900 px-3 py-2 bg-white"><option value="">— Aucun —</option>{parents.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}</select></Field>
              <Field label="Téléphone parent"><input data-testid="child-form-parent_phone" value={form.parent_phone || ""} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} placeholder="+33 ..." className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Personnes autorisées à récupérer"><input data-testid="child-form-authorized_pickup" value={form.authorized_pickup || ""} onChange={(e) => setForm({ ...form, authorized_pickup: e.target.value })} placeholder="Maman, Papa, Mamie..." className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <Field label="Besoins spécifiques"><input data-testid="child-form-special_needs" value={form.special_needs || ""} onChange={(e) => setForm({ ...form, special_needs: e.target.value })} placeholder="Allergie, PMR..." className="w-full border border-zinc-900 px-3 py-2" /></Field>
              <div className="sm:col-span-2"><Field label="Remarques de sécurité"><textarea data-testid="child-form-safety_notes" rows={2} value={form.safety_notes || ""} onChange={(e) => setForm({ ...form, safety_notes: e.target.value })} placeholder="Ventoline, traitement..." className="w-full border border-zinc-900 px-3 py-2 resize-none" /></Field></div>
            </div>
            <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
              <button type="button" onClick={close} className="px-4 py-2 border border-zinc-900 font-semibold hover:bg-zinc-100">Annuler</button>
              <button data-testid="save-child-btn" type="submit" className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 font-bold hover:bg-[#ffd43a]">Enregistrer</button>
            </div>
          </form>
        </div>
      )}

      {showQr && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="qr-modal">
          <div className="bg-white border border-zinc-900 w-full max-w-sm">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">QR — {showQr.first_name}</div>
              <button onClick={() => setShowQr(null)}><X size={18} /></button>
            </div>
            <div className="p-6 flex flex-col items-center gap-4">
              <QRCodeSVG value={showQr.qr_code} size={220} bgColor="#ffffff" fgColor="#0A0A0B" level="M" />
              <div className="font-mono text-xs tracking-[0.15em] uppercase">{showQr.qr_code}</div>
              <p className="text-xs text-zinc-500 text-center">À présenter au chauffeur pour la montée / descente.</p>
            </div>
          </div>
        </div>
      )}

      {showDetail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" data-testid="child-detail-modal">
          <div className="bg-white border border-zinc-900 w-full max-w-lg my-8">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl">{showDetail.first_name} {showDetail.last_name}</div>
              <button onClick={() => setShowDetail(null)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2"><School size={14} /> <span className="text-zinc-500 font-mono text-xs uppercase tracking-widest">{showDetail.grade}</span> · {showDetail.school_name}</div>
              {showDetail.birth_date && <div className="flex items-center gap-2"><Cake size={14} /> Né(e) le {showDetail.birth_date}</div>}
              <div className="flex items-center gap-2"><MapPin size={14} /> {showDetail.home_address}</div>
              {showDetail.drop_address && <div className="flex items-center gap-2 text-zinc-600"><MapPin size={14} /> → {showDetail.drop_address}</div>}
              <div className="flex items-center gap-2"><Clock size={14} /> ↑ {showDetail.schedule_morning || "—"} · ↓ {showDetail.schedule_evening || "—"}</div>
              <div className="flex items-center gap-2"><Phone size={14} /> {showDetail.parent_phone || "—"}</div>
              <div className="border-t border-zinc-200 pt-2 mt-2">
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Personnes autorisées</div>
                <div>{showDetail.authorized_pickup || "—"}</div>
              </div>
              {(showDetail.special_needs || showDetail.safety_notes) && (
                <div className="border border-[#FFC60A] bg-[#FFF7D6] p-3">
                  <div className="text-[10px] font-mono tracking-[0.2em] uppercase">⚠ Sécurité</div>
                  {showDetail.special_needs && <div className="text-sm mt-1"><b>Besoin :</b> {showDetail.special_needs}</div>}
                  {showDetail.safety_notes && <div className="text-sm mt-1"><b>Note :</b> {showDetail.safety_notes}</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
